import React, { useEffect, useState } from 'react'
import { Alert, Button, ProgressBar } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { SETTINGS_KEYS } from '../../config/environment'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { settingsService } from '../../services/settings.service'
import { websocketService } from '../../services/websocket.service'
import './ConsumerStatusWidget.scss'

interface DocumentStatus {
  filename: string
  documentId?: number
  message?: string
  taskId?: string
  phase?: string
  currentPhaseProgress?: number
  currentPhaseMaxProgress?: number
  getProgress?(): number
}

export const ConsumerStatusWidget: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [statuses, setStatuses] = useState<DocumentStatus[]>([])

  const currentOrgId = settingsService.getCurrentOrganizationId()

  useEffect(() => {
    setStatuses([])

    const updateStatuses = () => {
      const newStatuses = websocketService.getConsumerStatus()
      setStatuses(newStatuses)
    }

    updateStatuses()
    const interval = setInterval(updateStatuses, 500)

    return () => clearInterval(interval)
  }, [currentOrgId])

  const suppressOnDashboard = settingsService.get(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_SUPPRESS_ON_DASHBOARD)
  const isOnDashboard = location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/')

  if (statuses.length === 0) {
    return null
  }

  if (suppressOnDashboard && isOnDashboard) {
    return null
  }

  const getStatusSummary = (): string => {
    const notCompleted = websocketService.getConsumerStatusNotCompleted()
    const failed = statuses.filter((s) => s.phase === 'FAILED')
    const success = statuses.filter((s) => s.phase === 'SUCCESS')

    const parts: string[] = []
    if (notCompleted.length > 0) {
      parts.push(`${t('processing')}: ${notCompleted.length}`)
    }
    if (failed.length > 0) {
      parts.push(`${t('failed')}: ${failed.length}`)
    }
    if (success.length > 0) {
      parts.push(`${t('added')}: ${success.length}`)
    }
    return parts.join(' • ')
  }

  const getStatusColor = (status: DocumentStatus): string => {
    switch (status.phase) {
      case 'UPLOADING':
      case 'STARTED':
      case 'WORKING':
        return 'primary'
      case 'FAILED':
        return 'danger'
      case 'SUCCESS':
        return 'success'
      default:
        return 'secondary'
    }
  }

  const isFinished = (status: DocumentStatus): boolean => {
    return status.phase === 'FAILED' || status.phase === 'SUCCESS'
  }

  const getProgress = (status: DocumentStatus): number => {
    if (status.getProgress) {
      return status.getProgress()
    }
    const phase = status.phase
    if (phase === 'STARTED') return 0.0
    if (phase === 'UPLOADING') {
      return ((status.currentPhaseProgress || 0) / (status.currentPhaseMaxProgress || 1)) * 0.2
    }
    if (phase === 'WORKING') {
      return ((status.currentPhaseProgress || 0) / (status.currentPhaseMaxProgress || 1)) * 0.8 + 0.2
    }
    if (phase === 'SUCCESS' || phase === 'FAILED') return 1.0
    return 0.0
  }

  const handleDismiss = (status: DocumentStatus) => {
    websocketService.dismiss(status)
    setStatuses(websocketService.getConsumerStatus())
  }

  const handleDismissCompleted = () => {
    websocketService.dismissCompleted()
    setStatuses(websocketService.getConsumerStatus())
  }

  const completed = websocketService.getConsumerStatusCompleted()

  return (
    <div className="consumer-status-widget">
      <div className="consumer-status-widget__header">
        <div className="consumer-status-widget__header-left">
          <div className="consumer-status-widget__icon">
            <i className="bi bi-file-earmark-arrow-up"></i>
          </div>
          <div>
            <h5 className="consumer-status-widget__title">{t('document_processing')}</h5>
            {statuses.length > 0 && <p className="consumer-status-widget__summary">{getStatusSummary()}</p>}
          </div>
        </div>
        {completed.length > 0 && (
          <button
            className="btn btn-link btn-sm p-0 text-decoration-none consumer-status-widget__dismiss-all"
            onClick={handleDismissCompleted}
            title={t('dismiss_completed')}
          >
            <span className="me-1">{t('dismiss_completed')}</span>
            <i className="bi bi-check2-all"></i>
          </button>
        )}
      </div>
      <div className="consumer-status-widget__body">
        <div className="consumer-status-widget__items">
          {statuses.map((status, index) => (
            <div key={status.taskId || status.filename || index} className="consumer-status-widget__item">
              <Alert variant={getStatusColor(status) as any} className="consumer-status-widget__alert">
                <div className="consumer-status-widget__alert-header">
                  <Alert.Heading className="consumer-status-widget__alert-heading">{status.filename}</Alert.Heading>
                  {isFinished(status) && (
                    <button
                      type="button"
                      className="btn-close consumer-status-widget__close-btn"
                      onClick={() => handleDismiss(status)}
                      aria-label={t('close')}
                    ></button>
                  )}
                </div>
                {(!isFinished(status) || (isFinished(status) && !status.documentId)) && status.message && (
                  <p
                    className="consumer-status-widget__alert-message"
                    title={t(status.message, { defaultValue: status.message })}
                  >
                    {t(status.message, { defaultValue: status.message })}
                  </p>
                )}
                <ProgressBar now={getProgress(status) * 100} variant={getStatusColor(status) as any} className="consumer-status-widget__progress" />
                {permissionsService.currentUserCan(PermissionAction.View, PermissionType.Document) && (
                  <>
                    {isFinished(status) && status.documentId && !websocketService.isTaskMarkedForAutoOpen(status.taskId) && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="consumer-status-widget__open-btn"
                        onClick={() => {
                          handleDismiss(status)
                          navigate(`/documents/${status.documentId}/details`)
                        }}
                      >
                        <i className="bi bi-file-text"></i> {t('open')}
                      </Button>
                    )}
                  </>
                )}
              </Alert>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
